import { type Evaluated, isDerived, parentsOf } from '@/domain/construction';
import { type EntityId, type SceneDocument } from '@/domain/model';

import { type EditAction } from './actions';

/**
 * Cutting a construction loose from what it was built on.
 *
 * ## The report
 *
 * "tại sao khi di chuyển khối hộp nó lại di chuyển cả khối đa giác tôi vẽ, làm sao để nó liên
 * kết và cách bỏ liên kết giữa 2 khối".
 *
 * Nothing was wrong. Drawing a polygon by clicking on a box's edges does not put its corners
 * *near* the box — snapping makes them points **on** those edges, so they are part of the box's
 * construction and move with it. That is the mechanism the whole editor is built on (ADR-0003):
 * a point dropped on a circle stays on that circle forever, and a figure that merely looked
 * attached would be a drawing program.
 *
 * What was missing is the way out. Linking is a gesture — click the edge — and unlinking was
 * nothing at all: the only route was to delete the shape and draw it again holding Ctrl. A
 * mechanism with no undo is a trap however good the mechanism.
 *
 * ## What it does
 *
 * Replaces the derived points the selection is built on with **free points in the same places**.
 * The figure does not move a pixel; it simply stops following. Undo puts the link back, which is
 * what makes it safe to try.
 *
 * ## What it deliberately does not do
 *
 * It does not walk further up. A polygon on a box's edges sits on `ratio-point`s, which sit on
 * the box's corners; detaching the *corners* would leave dots that stay behind when the box
 * moves, which is a second surprise in place of the first. Cutting the selection's own inputs is
 * enough, and it leaves the box exactly as it was.
 *
 * It does not spare a point shared with something outside the selection. Sharing a point is a
 * statement — see `movableRootsOf` — and a detach that quietly duplicated it would leave two
 * points where the user put one, which is the failure this editor works hardest to avoid.
 */
export function detachActions(
  document: SceneDocument,
  values: ReadonlyMap<EntityId, Evaluated | null>,
  selection: Iterable<EntityId>
): readonly EditAction[] {
  const loosened = new Set<EntityId>();
  const actions: EditAction[] = [];

  for (const id of selection) {
    const entity = document.entities.get(id);
    if (entity === undefined) continue;

    for (const parent of parentsOf(entity.source)) {
      if (loosened.has(parent)) continue;

      const held = document.entities.get(parent);
      if (held === undefined || !isDerived(held.source)) continue;

      // Only points. A polygon built on a circle is cut loose by freeing the points it stands
      // on; freeing the circle itself would be answering a question nobody asked.
      const value = values.get(parent);
      if (value?.kind !== 'point') continue;

      loosened.add(parent);
      actions.push({
        kind: 'update-entity',
        entity: { ...held, source: { op: 'free-point', position: value.point } },
      });
    }
  }

  return actions;
}

/**
 * Whether anything in the selection is still tied to something else.
 *
 * So a menu can offer the command only when it would do something. An item that is present and
 * does nothing teaches a user that the menu lies.
 */
export function canDetach(
  document: SceneDocument,
  values: ReadonlyMap<EntityId, Evaluated | null>,
  selection: Iterable<EntityId>
): boolean {
  return detachActions(document, values, selection).length > 0;
}
